import "pages/StandardTreatment/shared/masterData.scss";
import "pages/MasterPengobatanStandar/index.scss";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { FiPlus } from "react-icons/fi";
import Button from "components/atoms/Button";
import ActionButton from "components/atoms/ActionButton";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import standardTreatmentService from "services/StandardTreatment";
import useServiceData from "../shared/useServiceData";
import useBootstrapModal from "../shared/useBootstrapModal";
import { ACTIVE_FILTERS, ErrorState } from "../shared/ui";
import RegimenFormModal, { REGIMEN_MODAL_ID } from "./RegimenFormModal";

const LIMIT = 10;

const MasterRegimen = () => {
  const history = useHistory();
  const [drugId, setDrugId] = useState("");
  const [isActive, setIsActive] = useState("");
  const [page, setPage] = useState(1);
  const { modal, open, setMode, close } = useBootstrapModal(REGIMEN_MODAL_ID);

  useEffect(() => {
    document.title = "SISMAL | Master Obat";
  }, []);

  // Sumber pilihan obat (semua status, agar aturan dosis lama tetap terbaca).
  const drugs = useServiceData(() => standardTreatmentService.listDrugs({ page: 1, limit: 100 }), []);
  const drugList = drugs.data?.drugs || [];
  const drugFilterOptions = [{ label: "Semua obat", value: "" }, ...drugList.map((d) => ({ label: d.name, value: d.id }))];

  // GET /regimens — filter obat memakai searchBy=drug_id.
  const { data, loading, error, reload } = useServiceData(
    () =>
      standardTreatmentService.listRegimens({
        page,
        limit: LIMIT,
        is_active: isActive || undefined,
        ...(drugId && { searchBy: "drug_id", searchValue: drugId }),
      }),
    [page, isActive, drugId]
  );

  const columns = useMemo(
    () => [
      {
        header: "Obat",
        size: 170,
        cell: ({ row }) => (
          <>
            <div>{row.original.drug?.name}</div>
            <small className="mps-muted">{row.original.drug?.code}</small>
          </>
        ),
      },
      { header: "Kode", accessorKey: "code", size: 140, cell: ({ getValue }) => <code className="md-code">{getValue()}</code> },
      { header: "Nama Aturan Dosis", accessorKey: "name", size: 240 },
      { header: "Dose Basis", accessorKey: "dose_basis", size: 110 },
      {
        header: "Status",
        accessorKey: "is_active",
        size: 90,
        cell: ({ getValue }) => (
          <span className={`mps-pill ${getValue() ? "is-success" : "is-neutral"}`}>{getValue() ? "Aktif" : "Nonaktif"}</span>
        ),
      },
      {
        header: "Aksi",
        size: 230,
        cell: ({ row }) => (
          <div className="row-actions">
            <ActionButton btnType="detail" onClick={() => open("detail", row.original)} />
            <ActionButton btnType="edit" onClick={() => open("edit", row.original)} />
            <ActionButton
              btnType="doseBand"
              onClick={() => history.push(`/dosis-standar?regimen_id=${row.original.id}`)}
            />
          </div>
        ),
      },
    ],
    [history]
  );

  const tableInstance = useReactTable({ data: data?.regimens || [], columns, getCoreRowModel: getCoreRowModel() });

  const saveRegimen = (body) =>
    (modal.mode === "edit"
      ? standardTreatmentService.updateRegimen(modal.item.id, body)
      : standardTreatmentService.createRegimen(body)
    ).then(() => {
      close();
      toast.success(`Aturan dosis ${body.code} berhasil disimpan`);
      reload();
    });

  return (
    <div className="master-data-page">
      <h1 className="page-title">Master Obat</h1>
      <p className="page-subtitle">
        Satu aturan dosis milik satu obat dan berisi dosis standar per berat badan. Dipakai Terapi
        DOSE_BAND. Aturan dosis tidak dihapus — nonaktifkan bila sudah tidak dipakai.
      </p>

      <div className="action-wrapper">
        <Button variant="primary" size="normal" onClick={() => open("add")} isDisabled={!drugList.length}>
          <FiPlus size={16} className="mr-2" />
          Tambah Aturan Dosis
        </Button>
        <div className="action-filters">
          <div className="action-search">
            <InputSelect
              isClearable={false}
              aria-label="Filter obat"
              data={drugFilterOptions}
              value={drugFilterOptions.find((o) => o.value === drugId)}
              onChange={(sel) => {
                setDrugId(sel?.value ?? "");
                setPage(1);
              }}
            />
          </div>
          <div className="action-status">
            <InputSelect
              isClearable={false}
              aria-label="Filter status"
              data={ACTIVE_FILTERS}
              value={ACTIVE_FILTERS.find((o) => o.value === isActive)}
              onChange={(sel) => {
                setIsActive(sel?.value ?? "");
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {error || drugs.error ? (
        <div className="mt-4">
          <ErrorState messages={error || drugs.error} onRetry={() => (error ? reload() : drugs.reload())} />
        </div>
      ) : (
        <TableDataComponent
          columnLength={columns.length}
          tableInstance={tableInstance}
          onPageChange={(next) => next && setPage(next)}
          loading={loading}
          currentPage={data?.currentPage ?? page}
          limit={LIMIT}
          nextPage={data?.nextPage}
          previousPage={data?.previousPage}
          total={data?.total ?? 0}
        />
      )}

      <RegimenFormModal
        key={modal.openCount}
        mode={modal.mode}
        regimen={modal.item}
        drugs={drugList}
        onSubmit={saveRegimen}
        onEdit={() => setMode("edit")}
      />
    </div>
  );
};

export default MasterRegimen;
